import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Icon } from "@/lib/icons";
import { wordApi } from "@/api/word.api";

interface Props {
  fileId: number;
  fileName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  labels: { previewLoading: string; previewError: string; previewEmpty: string; previewSummary: string };
}

export function ExcelFilePreviewDialog({ fileId, fileName, open, onOpenChange, labels }: Props) {
  const [rows, setRows] = useState<string[][]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [state, setState] = useState<"idle" | "loading" | "ready" | "error">("idle");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setState("loading");
    setRows([]);
    setTotalRows(0);
    void wordApi.downloadFile(fileId).then(async ({ blob }) => {
      const workbook = XLSX.read(await blob.arrayBuffer(), { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const data = sheet ? XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1, defval: "" }) : [];
      if (!cancelled) {
        setRows(data.slice(0, 101));
        setTotalRows(data.length);
        setState("ready");
      }
    }).catch(() => {
      if (!cancelled) setState("error");
    });
    return () => { cancelled = true; };
  }, [fileId, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl overflow-hidden rounded-2xl border border-border/40 border-t-2 border-t-primary/70 bg-background p-0 shadow-none">
        <DialogHeader className="border-b border-border/40 px-6 py-5">
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Icon name="fileText" className="h-4 w-4 text-primary" />
            {fileName}
          </DialogTitle>
        </DialogHeader>
        <div className="mx-6 mb-6 max-h-[65vh] overflow-auto">
          {state === "loading" && <p className="p-6 text-sm text-muted-foreground">{labels.previewLoading}</p>}
          {state === "error" && <p className="p-6 text-sm text-destructive">{labels.previewError}</p>}
          {state === "ready" && rows.length === 0 && <p className="p-6 text-sm text-muted-foreground">{labels.previewEmpty}</p>}
          {state === "ready" && rows.length > 0 && (
            <>
            <p className="border-b border-border/30 px-3 py-2 text-[0.68rem] uppercase tracking-[0.16em] text-muted-foreground">{labels.previewSummary.replace("{visible}", String(rows.length)).replace("{total}", String(totalRows))}</p>
            <table className="w-full min-w-max border-collapse text-left text-xs">
              <tbody>
                {rows.map((row, rowIndex) => (
                  <tr key={rowIndex} className="border-b border-border/30 last:border-b-0">
                    {row.map((cell, cellIndex) => rowIndex === 0
                      ? <th key={cellIndex} className="sticky top-0 bg-background px-3 py-2 font-semibold text-foreground">{String(cell)}</th>
                      : <td key={cellIndex} className="px-3 py-2 text-muted-foreground">{String(cell)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
